/**
 * PanelManager — central orchestrator for management panels.
 *
 * Handles open/close lifecycle, shared backdrop, z-ordering, Escape-to-close,
 * and forced-open mode (used on first-launch class selection).
 *
 * @module ui/panels/PanelManager
 */

import { bus } from '../../core/events.js';

let instance = null;

export class PanelManager {
  constructor() {
    if (instance) return instance;
    /** @type {Map<string, import('./BasePanel.js').BasePanel>} */
    this._panels = new Map();
    this._activeName = null;
    this._backdrop = null;
    this._escHandler = null;
    this._ensureBackdrop();
    bus.on('panel:requestClose', ({ name }) => {
      if (this._activeName === name) this.close();
    });
    instance = this;
  }

  /** @param {string} name @param {import('./BasePanel.js').BasePanel} panel */
  register(name, panel) {
    this._panels.set(name, panel);
  }

  /**
   * Open a panel by name. Closes the currently-open one first.
   * @param {string} name
   * @param {{forced?:boolean}} [opts]
   */
  open(name, opts = {}) {
    if (this._activeName === name) {
      // Already open — focus it
      const p = this._panels.get(name);
      if (p) p.focus();
      return;
    }

    // Close current
    if (this._activeName) {
      const prev = this._panels.get(this._activeName);
      if (prev) prev._closeInternal();
    }

    const panel = this._panels.get(name);
    if (!panel) {
      console.warn(`PanelManager: no panel registered as "${name}"`);
      return;
    }

    this._activeName = name;
    panel._openInternal(opts);
    this._showBackdrop(!!opts.forced);
    this._bindEsc(!!opts.forced);
    bus.emit('panel:opened', { name });
  }

  /** Force-open a panel (no close-possible). Used for first-launch hero pick. */
  openForced(name) {
    this.open(name, { forced: true });
  }

  /** Close the currently-open panel. */
  close() {
    if (!this._activeName) return;
    const panel = this._panels.get(this._activeName);
    if (panel && panel._forcedOpen) return; // can't dismiss forced panels
    if (panel) panel._closeInternal();
    this._activeName = null;
    this._hideBackdrop();
    this._unbindEsc();
    bus.emit('panel:closed', {});
  }

  closeAll() {
    this.close();
  }

  isOpen(name) {
    return this._activeName === name;
  }

  get activeName() {
    return this._activeName;
  }

  // ---- internals ----

  _ensureBackdrop() {
    if (this._backdrop) return;
    this._backdrop = document.createElement('div');
    this._backdrop.className = 'pd-panel-backdrop';
    this._backdrop.addEventListener('pointerdown', () => this.close());
    // Append inside the PiP canvas area so the backdrop only covers the game,
    // not the entire page. Panels open INSIDE the PiP.
    const panelsContainer = document.getElementById('pd-panels');
    if (panelsContainer) {
      panelsContainer.appendChild(this._backdrop);
    } else {
      document.body.appendChild(this._backdrop);
    }
  }

  _showBackdrop(forced) {
    if (!this._backdrop) return;
    this._backdrop.classList.add('active');
    if (forced) this._backdrop.style.pointerEvents = 'none';
    else this._backdrop.style.pointerEvents = 'auto';
  }

  _hideBackdrop() {
    if (!this._backdrop) return;
    this._backdrop.classList.remove('active');
  }

  _bindEsc(forced) {
    if (this._escHandler) document.removeEventListener('keydown', this._escHandler);
    if (forced) return; // Escape doesn't close forced panels
    this._escHandler = (e) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._escHandler);
  }

  _unbindEsc() {
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
  }
}

/** Shared singleton. */
export const panelManager = new PanelManager();
